import { useState, useMemo, useRef, useEffect } from 'react';
import { Check, ChevronDown, ChevronUp, Search, X, Plus, Sparkles } from 'lucide-react';

export interface MultiSelectOption {
  id: string;
  name: string;
  isCustom?: boolean;
}

/**
 * A modern, premium multi-select checkbox grid with search filtering,
 * select-all/clear actions, dynamic "Others" custom text entry, and responsive layout.
 */
export default function MultiSelect({
  options = [],
  selected,
  onChange,
  emptyHint,
  collapseAfter = 9,
  onCreateOption,
  customLabel,
  customPlaceholder,
}: {
  options: MultiSelectOption[] | undefined;
  selected: string[];
  onChange: (ids: string[]) => void;
  emptyHint?: string;
  collapseAfter?: number;
  onCreateOption?: (name: string) => Promise<MultiSelectOption | null | void> | MultiSelectOption | null | void;
  customLabel?: string;
  customPlaceholder?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState('');
  const [customList, setCustomList] = useState<MultiSelectOption[]>([]);
  const [customText, setCustomText] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [otherPromptOpen, setOtherPromptOpen] = useState(false);
  const customInputRef = useRef<HTMLInputElement>(null);

  // Combine parent options with dynamically created custom options
  const allOptions = useMemo(() => {
    const list = [...(options ?? [])];
    for (const c of customList) {
      if (!list.some((o) => o.id === c.id || o.name.toLowerCase() === c.name.toLowerCase())) {
        list.push(c);
      }
    }
    return list;
  }, [options, customList]);

  // Find the "Others" option in the list if available
  const otherOption = useMemo(() => {
    return allOptions.find((o) => /^others?$/i.test(o.name.trim()));
  }, [allOptions]);

  const isOtherSelected = Boolean(
    (otherOption && selected.includes(otherOption.id)) || otherPromptOpen || customList.length > 0,
  );

  const filteredOptions = useMemo(() => {
    if (!allOptions.length) return [];
    if (!search.trim()) return allOptions;
    const q = search.toLowerCase();
    return allOptions.filter((o) => o.name.toLowerCase().includes(q));
  }, [allOptions, search]);

  // Auto-focus input when Others is opened
  useEffect(() => {
    if (isOtherSelected && customInputRef.current) {
      customInputRef.current.focus();
    }
  }, [isOtherSelected]);

  if (!options) {
    return <div className="skeleton" style={{ height: 64, borderRadius: 'var(--radius-sm)' }} />;
  }

  if (!options.length && !customList.length) {
    return (
      <div style={{ fontSize: 12.5, color: 'var(--text-faint)', fontStyle: 'italic', padding: '8px 0' }}>
        {emptyHint ?? 'Nothing to choose from yet.'}
      </div>
    );
  }

  const toggle = (id: string) => {
    const isOther = otherOption?.id === id;
    if (selected.includes(id)) {
      onChange(selected.filter((x) => x !== id));
      if (isOther) {
        setOtherPromptOpen(false);
      }
    } else {
      onChange([...selected, id]);
      if (isOther) {
        setOtherPromptOpen(true);
        setTimeout(() => customInputRef.current?.focus(), 50);
      }
    }
  };

  const selectAll = () => {
    const allIds = Array.from(new Set([...selected, ...filteredOptions.map((o) => o.id)]));
    onChange(allIds);
  };

  const clearAll = () => {
    const filteredIds = new Set(filteredOptions.map((o) => o.id));
    onChange(selected.filter((id) => !filteredIds.has(id)));
  };

  const handleAddCustom = async () => {
    const trimmed = customText.trim();
    if (!trimmed || isAdding) return;

    setIsAdding(true);
    try {
      // Check if item already exists in options
      const existing = allOptions.find(
        (o) => o.name.toLowerCase() === trimmed.toLowerCase(),
      );

      let createdOpt: MultiSelectOption;

      if (existing) {
        createdOpt = existing;
      } else if (onCreateOption) {
        const res = await onCreateOption(trimmed);
        if (res && typeof res === 'object' && 'id' in res) {
          createdOpt = { ...res, isCustom: true };
        } else {
          createdOpt = {
            id: 'custom_' + Date.now() + '_' + Math.random().toString(36).substring(2, 6),
            name: trimmed,
            isCustom: true,
          };
        }
      } else {
        createdOpt = {
          id: 'custom_' + Date.now() + '_' + Math.random().toString(36).substring(2, 6),
          name: trimmed,
          isCustom: true,
        };
      }

      setCustomList((prev) => {
        if (prev.some((p) => p.id === createdOpt.id)) return prev;
        return [...prev, createdOpt];
      });

      // Ensure both the new custom option AND Others remain selected
      const nextIds = new Set(selected);
      nextIds.add(createdOpt.id);
      if (otherOption) nextIds.add(otherOption.id);
      onChange(Array.from(nextIds));

      // Reset text field and keep focus ready for entering the next one
      setCustomText('');
      setOtherPromptOpen(true);
      setTimeout(() => customInputRef.current?.focus(), 30);
    } catch {
      // Handled by parent toast/error if needed
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveCustom = (id: string) => {
    setCustomList((prev) => prev.filter((c) => c.id !== id));
    onChange(selected.filter((x) => x !== id));
  };

  const showSearch = allOptions.length > 6;
  const isOverflowing = !search && allOptions.length > collapseAfter;
  const visibleOptions = isOverflowing && !expanded ? filteredOptions.slice(0, collapseAfter) : filteredOptions;

  return (
    <div className="vns-multiselect">
      {/* Toolbar with Quick Search & Actions */}
      {(showSearch || selected.length > 0) && (
        <div className="vns-multiselect-toolbar">
          {showSearch && (
            <div className="vns-multiselect-search">
              <Search size={13} style={{ color: 'var(--text-muted)' }} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search options..."
                aria-label="Filter options"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    color: 'var(--text-muted)',
                    display: 'flex',
                  }}
                >
                  <X size={12} />
                </button>
              )}
            </div>
          )}

          <div className="vns-multiselect-actions">
            {filteredOptions.length > 1 && (
              <button type="button" onClick={selectAll} className="vns-multiselect-link-btn">
                Select all
              </button>
            )}
            {selected.length > 0 && (
              <button type="button" onClick={clearAll} className="vns-multiselect-link-btn">
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      {/* Grid of Option Chips */}
      {visibleOptions.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '12px 4px', fontStyle: 'italic' }}>
          No options matching "{search}"
        </div>
      ) : (
        <div className="vns-multiselect-grid">
          {visibleOptions.map((o) => {
            const on = selected.includes(o.id);
            return (
              <div
                key={o.id}
                role="checkbox"
                aria-checked={on}
                tabIndex={0}
                onClick={() => toggle(o.id)}
                onKeyDown={(e) => {
                  if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    toggle(o.id);
                  }
                }}
                className={`vns-multiselect-item ${on ? 'is-selected' : ''}`}
              >
                <div className="vns-multiselect-box">
                  {on && <Check size={11} strokeWidth={3.5} />}
                </div>
                <span className="vns-multiselect-label" title={o.name}>
                  {o.name}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Others / Custom Entry Input Box */}
      {isOtherSelected && (
        <div className="vns-multiselect-custom-box">
          <div className="vns-multiselect-custom-header">
            <span className="vns-multiselect-custom-title">
              <Sparkles size={13} color="#2563eb" />
              <span>Enter Custom {customLabel ?? 'Specialisation / Option'}</span>
            </span>
            <span className="vns-multiselect-custom-hint">
              Type name & press Enter to add multiple items
            </span>
          </div>

          <div className="vns-multiselect-custom-input-row">
            <input
              ref={customInputRef}
              type="text"
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleAddCustom();
                }
              }}
              className="vns-multiselect-custom-input"
            />
            <button
              type="button"
              onClick={() => void handleAddCustom()}
              disabled={!customText.trim() || isAdding}
              className="btn btn-primary btn-auto btn-sm vns-multiselect-custom-add-btn"
            >
              <Plus size={14} />
              <span>{isAdding ? 'Adding…' : 'Add'}</span>
            </button>
          </div>

          {/* List of Custom Added Tags */}
          {customList.length > 0 && (
            <div className="vns-multiselect-custom-chips">
              {customList.map((c) => (
                <span key={c.id} className="vns-multiselect-custom-chip">
                  <span>{c.name}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveCustom(c.id)}
                    title={`Remove ${c.name}`}
                  >
                    <X size={11} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Footer: Expand / Collapse Toggle & Selected Count */}
      <div className="vns-multiselect-footer">
        {isOverflowing ? (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="vns-multiselect-toggle"
          >
            {expanded ? (
              <>
                <span>Show fewer</span>
                <ChevronUp size={13} />
              </>
            ) : (
              <>
                <span>Show all {allOptions.length} options</span>
                <ChevronDown size={13} />
              </>
            )}
          </button>
        ) : (
          <div />
        )}

        <div className={`vns-multiselect-count ${selected.length > 0 ? 'has-count' : ''}`}>
          <span>
            {selected.length} of {allOptions.length} selected
          </span>
        </div>
      </div>
    </div>
  );
}
